import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, ChevronDown, ChevronUp, Sparkles, Users, IndianRupee, Cookie, AlertTriangle, RefreshCw, Calculator as CalculatorIcon, ChefHat, FileText, BookOpenText } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { isLaddooActive, formatCurrency } from "../lib/mdmData";
import { academicYearForDate } from "../lib/academicYear";
import { api } from "../lib/api";
import { exportLaddooPDF } from "../lib/mdmExport";
import { getSchoolInfo, saveSchoolInfo } from "../lib/schoolInfo";
import SchoolInfoDialog from "../components/SchoolInfoDialog";

const DEFAULT_RECIPE = [
  { name: "Ragi flour",            emoji: "🌾", grams_per_ladoo: 12.00, rate_per_ladoo: 1.80, is_service: false },
  { name: "Wheat flour",           emoji: "🌾", grams_per_ladoo: 12.00, rate_per_ladoo: 0.50, is_service: false },
  { name: "Sesame seeds",          emoji: "🧂", grams_per_ladoo:  1.00, rate_per_ladoo: 0.14, is_service: false },
  { name: "Sugar",                 emoji: "🍚", grams_per_ladoo:  6.00, rate_per_ladoo: 0.27, is_service: false },
  { name: "Ghee",                  emoji: "🧈", grams_per_ladoo:  2.00, rate_per_ladoo: 0.32, is_service: false },
  { name: "Groundnuts",            emoji: "🥜", grams_per_ladoo:  1.00, rate_per_ladoo: 0.15, is_service: false },
  { name: "Green cardamom powder", emoji: "🌿", grams_per_ladoo:  0.04, rate_per_ladoo: 0.06, is_service: false },
  { name: "Ladoo making charge",   emoji: "👨‍🍳", grams_per_ladoo:  0.00, rate_per_ladoo: 0.26, is_service: true },
];

const NORM_COST = 3.50; // Statutory cost cap per laddoo (₹)

export default function Laddoo() {
  const [date, setDate] = useState(new Date());
  const [students, setStudents] = useState("");
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [recipe, setRecipe] = useState(DEFAULT_RECIPE);
  const [advOpen, setAdvOpen] = useState(false);
  const [busy, setBusy] = useState(true);
  const [schoolInfo, setSchoolInfo] = useState(getSchoolInfo());
  const [schoolDialogOpen, setSchoolDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // "pdf" | "panji" | "both"
  const [savingPanji, setSavingPanji] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.getLaddooRecipe();
        if (Array.isArray(r?.ingredients) && r.ingredients.length) setRecipe(r.ingredients);
      } catch {
        // fallback to defaults silently
      } finally {
        setBusy(false);
      }
    })();
  }, []);

  const isLaddooDay = useMemo(() => isLaddooActive(date, date.getDay()), [date]);
  const studentCount = Math.max(0, Number(students) || 0);
  const totalRatePerLadoo = useMemo(() => recipe.reduce((s, i) => s + Number(i.rate_per_ladoo || 0), 0), [recipe]);
  const makingCharge = useMemo(() => recipe.find((i) => i.is_service)?.rate_per_ladoo || 0, [recipe]);
  const totalBudget = studentCount * totalRatePerLadoo;
  const totalMaking = studentCount * makingCharge;
  const costStatus = totalRatePerLadoo <= NORM_COST + 0.01 ? "ok" : "over";

  const updateItem = (idx, field, value) => {
    setRecipe((prev) => prev.map((it, i) => i === idx ? { ...it, [field]: Number(value) } : it));
  };
  const resetLocal = () => {
    // Reload from server (or defaults on failure)
    setBusy(true);
    api.getLaddooRecipe()
      .then((r) => setRecipe(Array.isArray(r?.ingredients) && r.ingredients.length ? r.ingredients : DEFAULT_RECIPE))
      .catch(() => setRecipe(DEFAULT_RECIPE))
      .finally(() => { setBusy(false); toast.info("Recipe reset to saved values"); });
  };

  const showReport = isLaddooDay && studentCount > 0;

  // ---- Actions: PDF export + Save to Poshan Panji ----
  const ay = useMemo(() => academicYearForDate(date), [date]);
  const weekdayName = format(date, "EEEE");

  const doExportPDF = async (info) => {
    try {
      await exportLaddooPDF({
        date, students: studentCount, recipe, school: info,
        academicYear: ay, weekdayName,
      });
      toast.success("Ladoo statement PDF downloaded");
    } catch (e) {
      toast.error("PDF export failed"); console.error(e);
    }
  };
  const doSavePanji = async (info) => {
    setSavingPanji(true);
    try {
      const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      const aggregated = {};
      recipe.forEach((it) => {
        aggregated[it.name] = {
          total_quantity: Number(it.grams_per_ladoo || 0) * studentCount,
          unit: it.is_service ? "svc" : "g",
          avg_rate: Number(it.rate_per_ladoo || 0),
          total_cost: Number(it.rate_per_ladoo || 0) * studentCount,
        };
      });
      await api.savePanjiEntry({
        udise: info.udise,
        school_name: info.name || "",
        date_iso: iso,
        weekday_name: weekdayName,
        academic_year: ay,
        menu_display: `🧁 Nutrition Ladoo × ${studentCount}`,
        counts: { balvatika: 0, primary: 0, upper_primary: 0, high_school: 0, ladoo: studentCount },
        summary: {
          total_students: studentCount,
          grand_total: studentCount * totalRatePerLadoo,
          avg_per_student: totalRatePerLadoo,
          total_rice_kg: 0,
          total_ladoos: studentCount,
        },
        aggregated_items: aggregated,
        entry_type: "ladoo",
      });
      toast.success("Saved to Poshan Panji register");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save to Panji failed");
    } finally { setSavingPanji(false); }
  };

  const runWithSchool = (info) => {
    saveSchoolInfo(info);
    setSchoolInfo(info);
    setSchoolDialogOpen(false);
    const action = pendingAction;
    setPendingAction(null);
    if (action === "pdf") doExportPDF(info);
    else if (action === "panji") doSavePanji(info);
    else if (action === "both") { doExportPDF(info); doSavePanji(info); }
  };

  const requestAction = (action) => {
    if (!showReport) return toast.error("Enter students on a Ladoo day first");
    // If we already have valid school info, run immediately
    if (schoolInfo?.name && /^\d{11}$/.test(schoolInfo?.udise || "")) {
      if (action === "pdf") return doExportPDF(schoolInfo);
      if (action === "panji") return doSavePanji(schoolInfo);
      if (action === "both") { doExportPDF(schoolInfo); return doSavePanji(schoolInfo); }
    }
    setPendingAction(action);
    setSchoolDialogOpen(true);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="laddoo-page">
      <div>
        <div className="chip chip-saffron"><Cookie size={14}/> Nutrition Ladoo Calculator</div>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Ladoo service day quantity &amp; budget</h1>
        <p className="mt-1 text-slate-600">Enter a date and total students. On Ladoo days (Tue / Thu / Sat), we compute per-ingredient quantity, cost and making charges.</p>
      </div>

      {/* Inputs */}
      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <Label className="text-xs uppercase tracking-widest font-bold text-slate-500">📅 Ladoo service date</Label>
          <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full h-12 mt-2 justify-start text-left font-semibold" data-testid="laddoo-date-btn">
                <CalendarIcon size={16}/> {format(date, "EEEE, dd MMM yyyy")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={date} onSelect={(d) => { if (d) { setDate(d); setDatePickerOpen(false); } }}
                        fromYear={2020} toYear={2100} captionLayout="dropdown-buttons"/>
            </PopoverContent>
          </Popover>
          <div className="mt-2">
            {isLaddooDay ? (
              <span className="chip chip-saffron" data-testid="laddoo-day-badge">🧁 Ladoo service day</span>
            ) : (
              <span className="chip chip-slate" data-testid="laddoo-nonday-badge">Not a Ladoo day (Tue / Thu / Sat only)</span>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-orange-200 bg-orange-50 p-5">
          <Label htmlFor="laddoo-students" className="text-xs uppercase tracking-widest font-bold text-orange-800">👥 Total students receiving laddoos</Label>
          <Input id="laddoo-students" type="number" min="0" step="1" value={students}
                 onChange={(e) => setStudents(e.target.value)}
                 placeholder="Enter number of students"
                 className="mt-2 h-12 bg-white font-display text-xl font-bold" data-testid="laddoo-students-input"/>
          <p className="mt-2 text-xs text-orange-900/80">One laddoo per student per service day.</p>
        </div>
      </div>

      {/* Warning if not a laddoo day */}
      {!isLaddooDay && (
        <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5 flex items-start gap-3" data-testid="laddoo-warning">
          <AlertTriangle size={20} className="text-amber-700 shrink-0 mt-0.5"/>
          <div>
            <div className="font-display font-bold text-amber-900">🚫 This day is not a Ladoo service day</div>
            <div className="text-sm text-amber-900/80 mt-0.5">Laddoos are served on Tuesday, Thursday and Saturday. Pick one of those days to run the calculator.</div>
          </div>
        </div>
      )}

      {/* Report */}
      {showReport && (
        <div className="mt-6 space-y-6 fade-rise" data-testid="laddoo-report">
          {/* Action bar */}
          <div className="flex flex-wrap gap-2 justify-end">
            <Button onClick={() => requestAction("panji")} disabled={savingPanji}
                    variant="outline" className="border-emerald-700 text-emerald-800 hover:bg-emerald-50"
                    data-testid="laddoo-save-panji-btn">
              <BookOpenText size={16}/> {savingPanji ? "Saving…" : "Save to Poshan Panji"}
            </Button>
            <Button onClick={() => requestAction("pdf")}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white"
                    data-testid="laddoo-pdf-btn">
              <FileText size={16}/> Download 1-page PDF
            </Button>
            <Button onClick={() => requestAction("both")}
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                    data-testid="laddoo-pdf-panji-btn">
              <Sparkles size={16}/> PDF + Save to Panji
            </Button>
          </div>

          {/* KPI cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Kpi icon={<CalendarIcon size={16}/>} label="Date" value={format(date, "d MMM yy")} tint="slate"/>
            <Kpi icon={<Users size={16}/>} label="Students" value={studentCount} tint="emerald" testid="laddoo-kpi-students"/>
            <Kpi icon={<Cookie size={16}/>} label="Total Ladoos" value={studentCount} tint="orange" testid="laddoo-kpi-ladoos"/>
            <Kpi icon={<IndianRupee size={16}/>} label="Total Budget" value={formatCurrency(totalBudget)} tint="emerald" testid="laddoo-kpi-budget"/>
            <Kpi
              icon={<CalculatorIcon size={16}/>}
              label="Per-Ladoo Cost"
              value={formatCurrency(totalRatePerLadoo)}
              tint={costStatus === "ok" ? "emerald" : "red"}
              testid="laddoo-kpi-per-ladoo"
              badge={costStatus === "ok" ? "✅ Within norm" : `⚠ ₹${(totalRatePerLadoo - NORM_COST).toFixed(2)} over norm`}
            />
            <Kpi icon={<ChefHat size={16}/>} label="Making Charges" value={formatCurrency(totalMaking)} tint="orange" testid="laddoo-kpi-making"/>
          </div>

          {/* Advanced */}
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <button onClick={() => setAdvOpen((v) => !v)}
                    className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                    data-testid="laddoo-adv-toggle">
              <span className="font-display font-bold text-slate-900 flex items-center gap-2">
                ⚙️ Advanced Recipe Settings <span className="text-xs text-slate-500 font-normal">(one-off tweaks — not saved)</span>
              </span>
              {advOpen ? <ChevronUp size={18}/> : <ChevronDown size={18}/>}
            </button>
            {advOpen && (
              <div className="p-5 border-t border-slate-100 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="laddoo-adv-grid">
                {recipe.map((it, i) => (
                  <div key={it.name + i} className="rounded-lg border border-slate-200 p-3 bg-slate-50/50" data-testid={`laddoo-adv-item-${i}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{it.emoji || "•"}</span>
                      <span className="font-semibold text-slate-900 text-sm">{it.name}</span>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] uppercase font-bold text-slate-500">Grams / Ladoo</Label>
                        <Input type="number" step="0.01" min="0" value={it.grams_per_ladoo}
                               onChange={(e) => updateItem(i, "grams_per_ladoo", e.target.value)}
                               className="h-9 mt-1 bg-white" data-testid={`laddoo-adv-g-${i}`}/>
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase font-bold text-slate-500">Rate / Ladoo (₹)</Label>
                        <Input type="number" step="0.01" min="0" value={it.rate_per_ladoo}
                               onChange={(e) => updateItem(i, "rate_per_ladoo", e.target.value)}
                               className="h-9 mt-1 bg-white" data-testid={`laddoo-adv-r-${i}`}/>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="sm:col-span-2 lg:col-span-3 flex justify-end">
                  <Button variant="outline" onClick={resetLocal} data-testid="laddoo-adv-reset"><RefreshCw size={14}/> Reset to saved</Button>
                </div>
              </div>
            )}
          </div>

          {/* Tables */}
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
              <div className="p-5 border-b border-slate-100">
                <div className="chip chip-slate">📊 Per-Ladoo Recipe</div>
                <div className="mt-2 font-display font-bold text-lg text-slate-900">Ingredients per single Ladoo</div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <Th>Ingredient</Th><Th className="text-right">Grams / Ladoo</Th><Th className="text-right">Rate (₹)</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipe.map((it, i) => (
                      <tr key={it.name + i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`per-ladoo-row-${i}`}>
                        <Td><span className="mr-2">{it.emoji}</span>{it.name}</Td>
                        <Td className="text-right">{it.is_service ? "—" : `${Number(it.grams_per_ladoo).toFixed(2)} g`}</Td>
                        <Td className="text-right font-semibold">{formatCurrency(it.rate_per_ladoo)}</Td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-emerald-50">
                      <Td className="font-bold text-emerald-900">Total per Ladoo</Td>
                      <Td className="text-right font-bold text-emerald-900">
                        {recipe.reduce((s, i) => s + (i.is_service ? 0 : Number(i.grams_per_ladoo || 0)), 0).toFixed(2)} g
                      </Td>
                      <Td className="text-right font-display font-extrabold text-lg text-emerald-800" data-testid="laddoo-per-total">
                        {formatCurrency(totalRatePerLadoo)}
                      </Td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
              <div className="p-5 border-b border-slate-100">
                <div className="chip chip-green">📈 Single-Day Totals</div>
                <div className="mt-2 font-display font-bold text-lg text-slate-900">For {studentCount} student(s)</div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <Th>Ingredient</Th><Th className="text-right">Total Qty</Th><Th className="text-right">Total Cost (₹)</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipe.map((it, i) => {
                      const totalG = Number(it.grams_per_ladoo || 0) * studentCount;
                      const totalR = Number(it.rate_per_ladoo || 0) * studentCount;
                      const qtyLbl = it.is_service ? "—" : totalG >= 1000 ? `${(totalG / 1000).toFixed(3)} kg` : `${totalG.toFixed(2)} g`;
                      return (
                        <tr key={it.name + i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`totals-row-${i}`}>
                          <Td><span className="mr-2">{it.emoji}</span>{it.name}</Td>
                          <Td className="text-right">{qtyLbl}</Td>
                          <Td className="text-right font-semibold">{formatCurrency(totalR)}</Td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-emerald-50">
                      <Td className="font-bold text-emerald-900">Grand Total</Td>
                      <Td className="text-right font-bold text-emerald-900">
                        {((recipe.reduce((s, i) => s + (i.is_service ? 0 : Number(i.grams_per_ladoo || 0)), 0) * studentCount) / 1000).toFixed(3)} kg
                      </Td>
                      <Td className="text-right font-display font-extrabold text-lg text-emerald-800" data-testid="laddoo-totals-grand">
                        {formatCurrency(totalBudget)}
                      </Td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {busy && <div className="mt-4 text-xs text-slate-500 flex items-center gap-1"><Sparkles size={12}/> Loading latest recipe from admin…</div>}

      <SchoolInfoDialog
        open={schoolDialogOpen}
        onOpenChange={(o) => { setSchoolDialogOpen(o); if (!o) setPendingAction(null); }}
        initial={schoolInfo}
        onConfirm={runWithSchool}
        confirmLabel={pendingAction === "panji" ? "Save to Panji" : pendingAction === "both" ? "Download & Save" : "Download PDF"}
        description="These will appear on the letterhead of the Ladoo statement and identify the school in the Poshan Panji register."
      />
    </div>
  );
}

function Kpi({ icon, label, value, tint, testid, badge }) {
  const bg = tint === "emerald" ? "border-emerald-200 bg-emerald-50" :
             tint === "orange" ? "border-orange-200 bg-orange-50" :
             tint === "red" ? "border-red-200 bg-red-50" :
             "border-slate-200 bg-white";
  const badgeCls = tint === "red" ? "chip chip-red" : "chip chip-green";
  return (
    <div className={`rounded-xl border p-3 ${bg}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">{icon}{label}</div>
      <div className="mt-1 font-display font-extrabold text-lg text-slate-900">{value}</div>
      {badge && <div className={`mt-1 ${badgeCls} inline-flex text-[10px]`}>{badge}</div>}
    </div>
  );
}

const Th = ({ children, className = "", ...rest }) => <th className={`p-3 text-xs uppercase tracking-wider font-bold text-slate-500 ${className}`} {...rest}>{children}</th>;
const Td = ({ children, className = "", ...rest }) => <td className={`p-3 text-sm text-slate-700 ${className}`} {...rest}>{children}</td>;
